import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import PrismStatusBar from './PrismStatusBar';
import PrismProtocolSearch from './PrismProtocolSearch';
import PrismDocumentationPopover from './PrismDocumentationPopover';
import PrismControlPanel from './PrismControlPanel';
import KeyboardShortcuts from './KeyboardShortcuts';
import tutorialSystem from './utils/TutorialSystem';
import nudgesSystem from './utils/NudgesSystem';

export default function PrismDemoOverlay({ children }) {
  const urlParams = new URLSearchParams(window.location.search);
  const isDemoMode = urlParams.get('demo') === 'prism';
  const featuredDemo = urlParams.get('feature');
  const shouldStartTutorial = tutorialSystem.shouldAutoStart(urlParams);

  const [statusBarVisible, setStatusBarVisible] = useState(true);
  const [protocolSearchOpen, setProtocolSearchOpen] = useState(false);
  const [protocolSearchAnchor, setProtocolSearchAnchor] = useState(null);
  const [docAssistantOpen, setDocAssistantOpen] = useState(false);
  const [docAssistantAnchor, setDocAssistantAnchor] = useState(null);
  const [controlPanelOpen, setControlPanelOpen] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [timerSpeed, setTimerSpeed] = useState('1x');
  const [sectionsVisited, setSectionsVisited] = useState(5);
  const [complianceScore, setComplianceScore] = useState(92);
  
  // Feature toggles state
  const [featuresEnabled, setFeaturesEnabled] = useState({
    statusBar: true,
    protocolSearch: true,
    docAssistant: true,
    struggleDetection: false,
    ghostPath: false,
  });

  useEffect(() => {
    if (!isDemoMode) return;

    // Auto-start tutorial if requested
    if (shouldStartTutorial) {
      setTimeout(() => {
        tutorialSystem.start();
      }, 1000);
    }
    // Auto-open featured demo
    else if (featuredDemo === 'protocol-search') {
      setTimeout(() => setProtocolSearchOpen(true), 1000);
    } else if (featuredDemo === 'ai-notes') {
      setTimeout(() => setDocAssistantOpen(true), 1000);
    } else if (featuredDemo === 'controls') {
      setTimeout(() => setControlPanelOpen(true), 500);
    }

    // Session timer with speed multiplier
    const getInterval = () => {
      switch (timerSpeed) {
        case '5x':
          return 200; // 5x faster
        case '10x':
          return 100; // 10x faster
        default:
          return 1000; // Real-time
      }
    };

    const timer = setInterval(() => {
      setSessionTime((prev) => {
        const newTime = prev + 1;
        
        // Only check nudges if Struggle Detection is enabled
        if (featuresEnabled.struggleDetection) {
          nudgesSystem.checkAndShowNudge(newTime);
        }
        
        // Simulate sections being visited over time
        if (newTime === 60) setSectionsVisited(6);  // 1 minute
        if (newTime === 120) setSectionsVisited(7); // 2 minutes
        if (newTime === 180) setSectionsVisited(8); // 3 minutes (all sections)
        
        return newTime;
      });
    }, getInterval());

    return () => clearInterval(timer);
  }, [isDemoMode, featuredDemo, timerSpeed, featuresEnabled.struggleDetection]);

  // If not in demo mode, just render children normally
  if (!isDemoMode) {
    return children;
  }

  const handleFeatureToggle = (feature, state) => {
    setFeaturesEnabled(prev => ({ ...prev, [feature]: state }));
    
    if (feature === 'statusBar') setStatusBarVisible(state);
    if (feature === 'protocolSearch' && !state) setProtocolSearchOpen(false);
    if (feature === 'docAssistant' && !state) setDocAssistantOpen(false);
    if (feature === 'struggleDetection' && !state) {
      // Reset nudges when disabling
      nudgesSystem.reset();
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {children}

      {/* Status Bar - Top Sticky */}
      {statusBarVisible && (
        <PrismStatusBar
          sessionTime={sessionTime}
          sectionsVisited={sectionsVisited}
          complianceScore={complianceScore}
          featuresEnabled={featuresEnabled}
          onOpenDocAssistant={(anchorEl) => {
            if (!featuresEnabled.docAssistant) return;
            setDocAssistantAnchor(anchorEl);
            setDocAssistantOpen(true);
          }}
          onOpenProtocolSearch={(anchorEl) => {
            if (!featuresEnabled.protocolSearch) return;
            setProtocolSearchAnchor(anchorEl);
            setProtocolSearchOpen(true);
          }}
        />
      )}

      {/* Protocol Search Popover */}
      <PrismProtocolSearch
        anchorEl={protocolSearchAnchor}
        open={protocolSearchOpen}
        onClose={() => setProtocolSearchOpen(false)}
      />

      {/* Documentation Assistant Popover */}
      <PrismDocumentationPopover
        open={docAssistantOpen}
        onClose={() => setDocAssistantOpen(false)}
        anchorEl={docAssistantAnchor}
      />

      {/* Demo Control Panel */}
      <PrismControlPanel
        open={controlPanelOpen}
        onToggle={() => setControlPanelOpen(!controlPanelOpen)}
        onFeatureToggle={handleFeatureToggle}
        featuresEnabled={featuresEnabled}
        onOpenProtocolSearch={(anchorEl) => {
          setProtocolSearchAnchor(anchorEl);
          setProtocolSearchOpen(true);
        }}
        onOpenDocAssistant={(anchorEl) => {
          setDocAssistantAnchor(anchorEl);
          setDocAssistantOpen(true);
        }}
        timerSpeed={timerSpeed}
        onTimerSpeedChange={setTimerSpeed}
      />

      {/* Keyboard Shortcuts */}
      <KeyboardShortcuts
        onProtocolSearch={() => setProtocolSearchOpen(true)}
        onDocAssistant={() => {
          setDocAssistantAnchor(null); // No anchor for keyboard shortcut
          setDocAssistantOpen(true);
        }}
        enabled={isDemoMode}
      />
    </Box>
  );
}
